import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Shield,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Calendar,
  ArrowLeft,
  Plus,
  Loader2,
  FileText,
  Upload,
  Building2,
  RefreshCw,
  AlertCircle,
  Flame,
  DollarSign,
  Users,
  Wrench,
  Eye,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  governance: Users,
  insurance: Shield,
  financial: DollarSign,
  safety: Flame,
  maintenance: Wrench,
};

const CATEGORY_COLORS = {
  governance: 'bg-purple-100 text-purple-700',
  insurance: 'bg-blue-100 text-blue-700',
  financial: 'bg-emerald-100 text-emerald-700',
  safety: 'bg-red-100 text-red-700',
  maintenance: 'bg-amber-100 text-amber-700',
};

export default function ComplianceDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(hoaId || '');
  const [activeTab, setActiveTab] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [addForm, setAddForm] = useState({
    template_code: '',
    due_date: '',
    notes: ''
  });

  const token = localStorage.getItem('session_token');

  useEffect(() => {
    fetchBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuilding) {
      fetchComplianceData();
    }
  }, [selectedBuilding]);

  const fetchBuildings = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch templates
      const tmplRes = await fetch(`${API_URL}/api/agent-features/compliance/templates`, { headers });
      if (tmplRes.ok) setTemplates(await tmplRes.json());
      
      // For agents - get assigned buildings
      const buildingsRes = await fetch(`${API_URL}/api/managing-agents/my-buildings`, { headers });
      if (buildingsRes.ok) {
        const data = await buildingsRes.json();
        setBuildings(data || []);
        if (!selectedBuilding && data?.length > 0) {
          setSelectedBuilding(data[0].hoa_id);
        }
      }
      
      // For admin - use their HOA
      if (!selectedBuilding && user?.hoa_id) {
        setSelectedBuilding(user.hoa_id);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  };

  const fetchComplianceData = async () => {
    if (!selectedBuilding) return;
    
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      // Fetch items
      const itemsRes = await fetch(`${API_URL}/api/agent-features/compliance/list/${selectedBuilding}`, { headers });
      if (itemsRes.ok) {
        setItems(await itemsRes.json());
      } else if (itemsRes.status === 404) {
        // Initialize compliance if not exists
        await fetch(`${API_URL}/api/agent-features/compliance/init/${selectedBuilding}`, {
          method: 'POST',
          headers
        });
        const retryRes = await fetch(`${API_URL}/api/agent-features/compliance/list/${selectedBuilding}`, { headers });
        if (retryRes.ok) setItems(await retryRes.json());
      }
      
      // Fetch summary
      const summaryRes = await fetch(`${API_URL}/api/agent-features/compliance/summary/${selectedBuilding}`, { headers });
      if (summaryRes.ok) setSummary(await summaryRes.json());
      
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeCompliance = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agent-features/compliance/init/${selectedBuilding}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchComplianceData();
      }
    } catch (error) {
      toast.error('Failed to initialize compliance');
    }
  };

  const handleAddItem = async () => {
    if (!addForm.template_code || !addForm.due_date) {
      toast.error('Please select a compliance type and due date');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/agent-features/compliance/create?hoa_id=${selectedBuilding}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(addForm)
      });
      
      if (res.ok) {
        toast.success('Compliance item added');
        setShowAddDialog(false);
        setAddForm({ template_code: '', due_date: '', notes: '' });
        fetchComplianceData();
      }
    } catch (error) {
      toast.error('Failed to add compliance item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateStatus = async (complianceId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/agent-features/compliance/${complianceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      
      if (res.ok) {
        toast.success(`Item marked as ${status}`);
        fetchComplianceData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedItem) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(
        `${API_URL}/api/agent-features/compliance/${selectedItem.compliance_id}/document?document_type=evidence`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        }
      );
      
      if (res.ok) {
        toast.success('Document uploaded');
        setShowUploadDialog(false);
        fetchComplianceData();
      }
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const due = new Date(dateString);
    const now = new Date();
    const diff = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatusBadge = (item) => {
    const daysUntil = getDaysUntil(item.due_date);
    
    if (item.status === 'completed') {
      return <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>;
    }
    if (item.status === 'overdue' || daysUntil < 0) {
      return <Badge className="bg-red-100 text-red-700">Overdue</Badge>;
    }
    if (daysUntil <= 30) {
      return <Badge className="bg-amber-100 text-amber-700">Due Soon</Badge>;
    }
    if (item.status === 'in_progress') {
      return <Badge className="bg-blue-100 text-blue-700">In Progress</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-600">Pending</Badge>;
  };

  const filteredItems = items.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'overdue') return item.status === 'overdue' || getDaysUntil(item.due_date) < 0;
    if (activeTab === 'upcoming') return item.status !== 'completed' && getDaysUntil(item.due_date) <= 30 && getDaysUntil(item.due_date) >= 0;
    if (activeTab === 'completed') return item.status === 'completed';
    return item.category === activeTab;
  });

  const selectedBuildingName = buildings.find(b => b.hoa_id === selectedBuilding)?.name || 'Building';

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="compliance-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Compliance Dashboard</h1>
              <p className="text-slate-500 mt-1">Track regulatory compliance for your buildings</p>
            </div>
            <div className="flex gap-2">
              {buildings.length > 1 && (
                <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                  <SelectTrigger className="w-[200px]">
                    <Building2 className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Select building" />
                  </SelectTrigger>
                  <SelectContent>
                    {buildings.map(b => (
                      <SelectItem key={b.hoa_id} value={b.hoa_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{summary.compliance_rate}%</p>
                    <p className="text-xs text-slate-500">Compliance Rate</p>
                  </div>
                  <Shield className="w-8 h-8 text-indigo-500" />
                </div>
                <Progress value={summary.compliance_rate} className="mt-2 h-1" />
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('completed')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-emerald-600">{summary.completed}</p>
                    <p className="text-xs text-slate-500">Completed</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('all')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{summary.pending}</p>
                    <p className="text-xs text-slate-500">Pending</p>
                  </div>
                  <Clock className="w-8 h-8 text-slate-400" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('overdue')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-red-600">{summary.overdue}</p>
                    <p className="text-xs text-slate-500">Overdue</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('upcoming')}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{summary.upcoming_30_days}</p>
                    <p className="text-xs text-slate-500">Due in 30 Days</p>
                  </div>
                  <Calendar className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="overdue" className="text-red-600">Overdue</TabsTrigger>
            <TabsTrigger value="upcoming" className="text-amber-600">Due Soon</TabsTrigger>
            <TabsTrigger value="completed" className="text-emerald-600">Completed</TabsTrigger>
            <TabsTrigger value="governance">Governance</TabsTrigger>
            <TabsTrigger value="financial">Financial</TabsTrigger>
            <TabsTrigger value="safety">Safety</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : filteredItems.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Compliance Items</h3>
                  <p className="text-slate-500 mb-4">
                    {items.length === 0 
                      ? 'Initialize compliance tracking for this building.'
                      : 'No items match this filter.'}
                  </p>
                  {items.length === 0 && (
                    <Button onClick={handleInitializeCompliance}>
                      Initialize Compliance
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredItems.map(item => {
                  const CategoryIcon = CATEGORY_ICONS[item.category] || Shield;
                  const daysUntil = getDaysUntil(item.due_date);
                  
                  return (
                    <Card 
                      key={item.compliance_id} 
                      className={`hover:shadow-md transition-shadow ${
                        item.status === 'completed' ? 'opacity-75' : ''
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${CATEGORY_COLORS[item.category]}`}>
                            <CategoryIcon className="w-5 h-5" />
                          </div>
                          {getStatusBadge(item)}
                        </div>
                        
                        <h4 className="font-semibold text-slate-900 mb-1">{item.name}</h4>
                        <p className="text-sm text-slate-500 mb-3 line-clamp-2">{item.description}</p>
                        
                        <div className="flex items-center justify-between text-sm mb-3">
                          <span className="text-slate-500">Due: {formatDate(item.due_date)}</span>
                          {item.status !== 'completed' && daysUntil >= 0 && (
                            <span className={`font-medium ${daysUntil <= 30 ? 'text-amber-600' : 'text-slate-600'}`}>
                              {daysUntil} days left
                            </span>
                          )}
                        </div>
                        
                        {/* Documents */}
                        {item.documents?.length > 0 && (
                          <div className="flex items-center gap-1 mb-3">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="text-xs text-slate-500">{item.documents.length} document(s)</span>
                          </div>
                        )}
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          {item.status !== 'completed' && (
                            <>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setShowUploadDialog(true);
                                }}
                              >
                                <Upload className="w-3 h-3 mr-1" />
                                Upload
                              </Button>
                              <Button 
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleUpdateStatus(item.compliance_id, 'completed')}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Complete
                              </Button>
                            </>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => {
                              setSelectedItem(item);
                              setShowDetailDialog(true);
                            }}
                          >
                            <Eye className="w-3 h-3" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Add Item Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add Compliance Item</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Compliance Type *</Label>
                <Select 
                  value={addForm.template_code} 
                  onValueChange={(v) => setAddForm({...addForm, template_code: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={addForm.due_date}
                  onChange={(e) => setAddForm({...addForm, due_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Notes</Label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => setAddForm({...addForm, notes: e.target.value})}
                  className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleAddItem} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedItem?.name}</DialogTitle>
            </DialogHeader>
            {selectedItem && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2">
                  <Badge className={CATEGORY_COLORS[selectedItem.category]}>{selectedItem.category}</Badge>
                  {getStatusBadge(selectedItem)}
                </div>
                <p className="text-slate-600">{selectedItem.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Due Date:</span>
                    <p className="font-medium">{formatDate(selectedItem.due_date)}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Frequency:</span>
                    <p className="font-medium capitalize">{selectedItem.frequency}</p>
                  </div>
                </div>
                {selectedItem.completed_date && (
                  <div className="text-sm">
                    <span className="text-slate-500">Completed:</span>
                    <p className="font-medium">{formatDate(selectedItem.completed_date)} by {selectedItem.completed_by}</p>
                  </div>
                )}
                {selectedItem.documents?.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Documents</Label>
                    <div className="space-y-2">
                      {selectedItem.documents.map((doc, i) => (
                        <a 
                          key={i}
                          href={`${API_URL}${doc.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          <FileText className="w-4 h-4 text-slate-500" />
                          <span className="text-sm">{doc.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailDialog(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload Evidence Document</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-slate-500 mb-4">
                Upload supporting documents for: <strong>{selectedItem?.name}</strong>
              </p>
              <Input
                type="file"
                onChange={handleUploadDocument}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
